"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, PackageCheck, Clock, FileText, ShieldCheck, Camera, Truck, RotateCcw, Mail, Headphones } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: "easeOut"
    }
  }
};
const timelineData = [{
  stage: "DEPOT RECEIPT & CHECK",
  duration: "24–48 Hours",
  note: "Physical inspection of tool seals and serial tags"
}, {
  stage: "RMA APPROVAL",
  duration: "Same Business Day",
  note: "Direct authorization notice dispatched via email"
}, {
  stage: "CARD & BANK REFUND",
  duration: "3–5 Business Days",
  note: "Standard payment gateway settlement window"
}, {
  stage: "STORE CREDIT OPTION",
  duration: "Immediate",
  note: "Instant account balance for urgent tooling orders"
}];
const rmaSteps = [{
  step: "01",
  title: "Submit RMA form",
  detail: "Provide order number, item serial ID, and return reason."
}, {
  step: "02",
  title: "Apply pre-paid label",
  detail: "Print the generated return label and affix it to the exterior."
}, {
  step: "03",
  title: "Original crating",
  detail: "Pack all manual accessories and original protective foam inserts."
}, {
  step: "04",
  title: "Carrier drop-off",
  detail: "Hand over at any authorized regional freight or parcel hub."
}];
export default function Refund() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: "hidden",
    whileInView: "show",
    viewport: {
      once: true,
      amount: 0.15
    }
  };
  return <section id="return-refund-policy" data-nav="dark" className="bg-background text-foreground py-12 md:py-20 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="bg-card text-muted-foreground border-border font-mono text-xs uppercase tracking-widest px-2.5 py-1 rounded-sm">
              PROTOCOL // 02
            </Badge>
            <span className="font-mono text-xs text-muted-foreground tracking-wider uppercase">
              RMA &amp; Dispatch Standards
            </span>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-1.5 self-stretch bg-primary rounded-xs shrink-0" aria-hidden="true" />
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground leading-none">
                Return and replacement protocols
              </h2>
              <p className="mt-3 text-muted-foreground font-sans text-base sm:text-lg max-w-3xl leading-relaxed">
                Clear inspection windows, step-by-step RMA handling, and verified settlement timelines for all industrial tools and workshop equipment.
              </p>
            </div>
          </div>
        </div>

        {/* Three-Column Technical Grid */}
        <motion.div variants={containerVariants} {...animationProps} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {/* Card 1: Damaged & Incorrect Shipments */}
          <motion.div variants={itemVariants} className="flex">
            <Card className="flex-1 bg-card border-border rounded-sm shadow-md flex flex-col justify-between hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="p-2 bg-muted rounded-sm text-primary">
                    <AlertTriangle className="size-5" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground tracking-wider uppercase">
                    SPEC // CRITICAL-48H
                  </span>
                </div>
                <CardTitle className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-card-foreground">
                  Damaged &amp; Incorrect Items
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4 text-sm font-sans flex-1">
                <p className="text-muted-foreground leading-relaxed">
                  Inspect heavy equipment and parcel deliveries upon arrival. If damage occurred during transit or the wrong sku was dispatched, report it within 48 hours for immediate triage.
                </p>

                <div className="bg-muted/60 border border-border p-3.5 rounded-sm space-y-2">
                  <div className="flex items-center gap-2 text-foreground font-medium font-mono text-xs tracking-wide">
                    <Camera className="size-4 text-primary shrink-0" />
                    REQUIRED DOCUMENTATION
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-1.5 list-disc list-inside">
                    <li>High-resolution photo of exterior shipping crate / box</li>
                    <li>Close-up photo of damaged component or incorrect model tag</li>
                    <li>Image of legible carrier shipping label and serial barcode</li>
                  </ul>
                </div>

                <Alert className="bg-destructive/10 border-destructive/30 rounded-sm text-foreground py-2.5">
                  <Truck className="size-4 text-destructive" />
                  <AlertTitle className="font-mono text-xs font-bold uppercase tracking-wider text-destructive">
                    Carrier Claim Limit
                  </AlertTitle>
                  <AlertDescription className="text-xs text-muted-foreground">
                    Freight claims reported past 48 hours are subject to carrier carrier denial and extended review windows.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card 2: Return Protocol & RMA Checklist */}
          <motion.div variants={itemVariants} className="flex">
            <Card className="flex-1 bg-card border-border rounded-sm shadow-md flex flex-col justify-between hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="p-2 bg-muted rounded-sm text-primary">
                    <PackageCheck className="size-5" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground tracking-wider uppercase">
                    STEP-BY-STEP
                  </span>
                </div>
                <CardTitle className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-card-foreground">
                  RMA Return Protocol
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4 text-sm font-sans flex-1">
                <p className="text-muted-foreground leading-relaxed">
                  Standard 30-day return window applies to unused, uninstalled equipment in factory packaging. Follow the four-stage RMA verification process:
                </p>

                <div className="space-y-2.5">
                  {rmaSteps.map((stepItem, index) => <div key={stepItem.step} data-index={index} className="flex items-start gap-3 p-2.5 bg-muted/40 border border-border rounded-sm">
                      <span className="font-mono text-xs font-bold text-primary bg-background px-1.5 py-0.5 rounded-xs border border-border">
                        {stepItem.step}
                      </span>
                      <div className="text-xs">
                        <span className="font-semibold text-foreground block uppercase font-mono">
                          {stepItem.title}
                        </span>
                        <span className="text-muted-foreground">{stepItem.detail}</span>
                      </div>
                    </div>)}
                </div>

                <div className="pt-1 flex items-center gap-2 text-xs font-mono text-muted-foreground">
                  <ShieldCheck className="size-4 text-primary shrink-0" />
                  <span>Tools must include all original wrenches, manuals &amp; bits.</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card 3: Refund & Credit Timelines */}
          <motion.div variants={itemVariants} className="flex">
            <Card className="flex-1 bg-card border-border rounded-sm shadow-md flex flex-col justify-between hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="p-2 bg-muted rounded-sm text-primary">
                    <Clock className="size-5" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground tracking-wider uppercase">
                    LEDGER METRICS
                  </span>
                </div>
                <CardTitle className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-card-foreground">
                  Refund Timelines &amp; Fees
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4 text-sm font-sans flex-1">
                <p className="text-muted-foreground leading-relaxed">
                  Every returned tool is benchmarked against standard specifications prior to issuing credit or bank reimbursement.
                </p>

                <div className="border border-border rounded-sm overflow-hidden">
                  <div className="bg-muted px-3 py-2 border-b border-border flex justify-between items-center text-xs font-mono font-bold text-foreground">
                    <span>STAGE</span>
                    <span>ESTIMATED WINDOW</span>
                  </div>
                  <div className="divide-y divide-border bg-card">
                    {timelineData.map((row, index) => <div key={row.stage} data-index={index} className="p-2.5 text-xs">
                        <div className="flex justify-between items-center font-mono font-semibold text-foreground">
                          <span className="text-[11px] text-foreground">{row.stage}</span>
                          <span className="text-primary font-bold">{row.duration}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{row.note}</p>
                      </div>)}
                  </div>
                </div>

                <div className="bg-muted/40 p-3 rounded-sm border border-border text-xs text-muted-foreground space-y-1">
                  <div className="font-mono font-semibold text-foreground flex items-center gap-1.5">
                    <FileText className="size-3.5 text-primary" />
                    RESTOCKING &amp; FREIGHT FEES
                  </div>
                  <p>
                    Standard orders carry 0% restocking fee. Custom fabricated mounts or heavy freight items returned for buyer preference incur a 15% inspection fee.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Technical Support CTA Strip */}
        <div className="bg-card border border-border p-6 md:p-8 rounded-sm shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1.5 max-w-2xl">
            <div className="flex items-center gap-2">
              <RotateCcw className="size-4 text-primary" />
              <span className="font-mono text-xs font-bold text-primary tracking-wider uppercase">
                DIRECT RMA ASSISTANCE
              </span>
            </div>
            <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase text-card-foreground tracking-tight">
              Ready to process an exchange or return?
            </h3>
            <p className="text-sm text-muted-foreground font-sans">
              Contact depot support with your order number to generate a return shipping label or check on an active RMA.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground font-serif font-bold uppercase tracking-wider rounded-sm px-6 py-2.5 h-auto transition-[color,background-color] shadow-sm">
              <Link to="/ContactPage">
                <Headphones className="size-4 mr-1.5" />
                Contact Depot
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-border bg-secondary hover:bg-accent text-secondary-foreground font-serif font-semibold uppercase tracking-wider rounded-sm px-5 py-2.5 h-auto transition-[color,background-color,border-color]">
              <a href="mailto:HugaScalet@proton.me">
                <Mail className="size-4 mr-1.5 text-primary" />
                Email Dispatch
              </a>
            </Button>
          </div>
        </div>

        <Separator className="mt-12 bg-border" />
      </div>
    </section>;
}